import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";

import { App } from "../model/app.js";

export type ActionEvent = CustomEvent<{
    appId: string;
}>;

@customElement("apps-list-component")
export class AppsListComponent extends LitElement {
    @property({ type: String }) term: string | undefined;
    @property({ type: Array }) apps: App[] = [];
    @property({ type: String }) action?: string;

    createRenderRoot() {
        return this;
    }

    render() {
        return html`
            <div class="flex flex-col">
                ${this.apps.map(
                    (app: App, index: number) => html`
                        <div class="flex flex-row items-center space-x-4 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"} py-2 px-4 rounded-lg">
                            <img
                                src=${app.icon}
                                alt="${app.localization?.title} App Icon"
                                class="w-14 h-14 rounded-2xl border border-gray-100"
                                loading="lazy"
                            />
                            <div class="flex-grow">
                                <div class="badge">${index + 1}</div>
                                <p class="text-base font-semibold">${this.highlightTerm(app.localization?.title || "--", this.term)}</p>
                                <p class="text-sm text-gray-500">${this.highlightTerm(app.localization?.subtitle || "--", this.term)}</p>
                                <div class="flex flex-row items-center space-x-2">
                                    <rating-component value="${app.localization?.ratingValue ?? 0}" max="5" step="0.1"></rating-component>
                                    <p class="text-sm text-gray-500">${this.shortenNumber(app.localization?.ratingCount ?? 0)}</p>
                                </div>
                            </div>

                            ${this.action
                                ? html` <button class="btn btn-primary" @click=${() => this.handleAppAction(app.id)}>${this.action}</button> `
                                : this.renderActions(app)}
                        </div>
                    `
                )}
            </div>
        `;
    }

    private renderActions(app: App) {
        return html`
            <div class="flex flex-row items-center gap-2">
                <a
                    href=${this.sensorTowerAppUrl(app)}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn-sm btn-circle"
                    title="Open SensorTower app page"
                    aria-label="Open SensorTower app page"
                >
                    <span class="text-xs font-semibold">ST</span>
                </a>
                <a
                    href="https://apps.apple.com/app/id${app.id}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn-sm btn-circle"
                    title="Open App Store page"
                    aria-label="Open App Store page"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                    </svg>
                </a>
            </div>
        `;
    }

    private sensorTowerAppUrl(app: App): string {
        const country = app.localization?.country?.toUpperCase() ?? "US";
        return `https://app.sensortower.com/overview/${encodeURIComponent(app.id)}?country=${encodeURIComponent(country)}`;
    }

    private highlightTerm(text: string, term: string | undefined): unknown {
        if (term == null) return text;
        if (!term.trim()) return text; // If no term is provided, return the original text

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special regex characters
        const regex = new RegExp(`(${escapedTerm})`, "gi"); // Create a case-insensitive regex
        const parts = text.split(regex);

        return parts.map((part, index) => (regex.test(part) ? html`<span class="bg-yellow-100">${part}</span>` : part));
    }

    private shortenNumber(value: number): string {
        if (value >= 1_000_000) {
            return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
        }
        if (value >= 1_000) {
            return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
        }
        return value.toString();
    }

    private handleAppAction(appId: string) {
        const detail = {
            appId: appId,
        };
        this.dispatchEvent(new CustomEvent("action", { detail, bubbles: true }));
    }
}
